'use client'

import { useEffect, useState, type RefObject } from 'react'

/**
 * true while the element is on screen. Goes back to false when it leaves,
 * so animations replay every time you scroll back to it (down or up).
 */
export function useInView(ref: RefObject<Element>, threshold = 0.25) {
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold })
    io.observe(el)
    return () => io.disconnect()
  }, [ref, threshold])
  return inView
}

/** Counts from 0 to `to` whenever `run` turns true. */
export function useCountUp(to: number, run: boolean, decimals = 0, ms = 1500) {
  const [v, setV] = useState(to)
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setV(to)
      return
    }
    if (!run) {
      setV(0)
      return
    }
    let raf = 0
    const start = performance.now()
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / ms)
      const eased = 1 - Math.pow(1 - p, 4)
      setV(Number((to * eased).toFixed(decimals)))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [to, run, decimals, ms])
  return v
}
